import { useRef } from 'react';
import { HilPresentationReplyEnvelopeWriter } from '@bizfirst/hil-core';
import { useHilContext } from '../context/HilContext';
import { useHilStore } from '../store/hilStore';

interface WriterCacheEntry {
  cacheKey: string;
  writer: HilPresentationReplyEnvelopeWriter;
}

/**
 * Returns the same writer instance across re-renders for a given active HIL session.
 * A new writer per render would silently discard _dataSteps accumulated by
 * earlier steps of a multi-step suspension (each addFormStep/addChatStep call
 * happens on the render-current writer, but advancing steps re-renders the
 * component, and useHilWriter used to hand back a fresh, empty writer).
 *
 * The cache key must not be EngageSessionID alone: the no-Engage resume path (e.g. Octopus AI
 * Agent chat) always carries EngageSessionID===0, so if the store's active session switches to a
 * *different* no-Engage suspended node/execution while the composer stays mounted, keying on
 * EngageSessionID alone would miss the switch and keep handing back a writer holding the
 * previous session's stale ExecutionResID/NodeKey — resuming the wrong execution. Including
 * ExecutionResID and NodeKey in the key disambiguates every no-Engage session from every other;
 * for the real-Engage path (Form/Approval), EngageSessionID alone already uniquely identifies
 * the session and the two extra fields are simply absent/unchanging, so behavior there is
 * unaffected.
 */
export function useHilWriter(): HilPresentationReplyEnvelopeWriter | null {
  const active = useHilStore((s) => s.active);
  const { submitClient } = useHilContext();
  const cacheRef = useRef<WriterCacheEntry | null>(null);

  if (!active) {
    cacheRef.current = null;
    return null;
  }

  const cacheKey = `${active.Session.EngageSessionID}:${active.Session.ExecutionResID ?? ''}:${active.Session.NodeKey ?? ''}`;
  if (!cacheRef.current || cacheRef.current.cacheKey !== cacheKey) {
    cacheRef.current = {
      cacheKey,
      writer: new HilPresentationReplyEnvelopeWriter({
        engageSessionID: active.Session.EngageSessionID,
        correlationID: active.Session.CorrelationID,
        resumeCallbackURL: active.Session.ResumeCallbackURL,
        processElementID: active.Session.ProcessElementID,
        // No-Engage resume path (e.g. Octopus AI Agent chat) — both undefined/no-op for every
        // session that has a real EngageSessionID.
        executionResID: active.Session.ExecutionResID,
        nodeKey: active.Session.NodeKey,
        submitClient,
      }),
    };
  }

  return cacheRef.current.writer;
}
